import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Settings, STAGE_DIMENSIONS } from "../settings";
import { PixiSettings } from "../pixiSettings";

export const DimensionsContext = createContext(null);

// Main context provider for responsive layout management
export const DimensionsProvider = ({ children }) => {
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial call

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate effective dimensions, constraining width to max stage width
  const dimensions = useMemo(() => {
    const effectiveWidth = Math.min(
      windowDimensions.width,
      STAGE_DIMENSIONS.MAX_WIDTH
    );
    const effectiveHeight = Math.min(
      windowDimensions.height,
      STAGE_DIMENSIONS.MAX_HEIGHT
    );

    return {
      width: effectiveWidth,
      height: effectiveHeight,
      windowWidth: windowDimensions.width,
      windowHeight: windowDimensions.height,
    };
  }, [windowDimensions]);

  // Calculate responsive layout mode based on device type and orientation
  const aspectRatioMode = useMemo(() => {
    const { width, height } = dimensions;
    const aspectRatio = width / height;
    const minDimension = Math.min(width, height);

    let deviceType;
    let layout;
    let orientation;

    // Device detection based on width
    // Desktop requires width >= 1367px (simplified - no aspect ratio requirement)
    if (width >= PixiSettings.ASPECT_RATIO_BREAKPOINTS.DESKTOP_MIN_WIDTH) {
      deviceType = PixiSettings.DEVICE.DESKTOP;
    } else if (minDimension <= PixiSettings.ASPECT_RATIO_BREAKPOINTS.PHONE) {
      deviceType = PixiSettings.DEVICE.MOBILE;
    } else {
      deviceType = PixiSettings.DEVICE.TABLET;
    }

    // Promote wide mobiles to tablets
    if (deviceType === PixiSettings.DEVICE.MOBILE) {
      const isSurfaceDuo =
        aspectRatio >= PixiSettings.DEVICE_DETECTION.SURFACE_DUO_ASPECT_RATIO &&
        minDimension >= PixiSettings.DEVICE_DETECTION.SURFACE_DUO_MIN_DIMENSION;
      const isLargeMobile =
        width >= PixiSettings.DEVICE_DETECTION.LARGE_MOBILE_WIDTH &&
        minDimension >=
          PixiSettings.DEVICE_DETECTION.LARGE_MOBILE_MIN_DIMENSION;

      if (isSurfaceDuo || isLargeMobile) {
        deviceType = PixiSettings.DEVICE.TABLET;
      }
    }

    // Handle near-square devices
    const isSquareDevice =
      Math.abs(aspectRatio - 1.0) <=
      PixiSettings.DEVICE_DETECTION.SQUARE_DEVICE_TOLERANCE;

    // Promote mobile square devices to tablet
    if (isSquareDevice && deviceType === PixiSettings.DEVICE.MOBILE) {
      deviceType = PixiSettings.DEVICE.TABLET;
    }

    // Orientation
    let isLandscape;
    // Only force landscape for near-square devices (generous tolerance)
    if (isSquareDevice && aspectRatio >= 0.85) {
      // Near-square devices (aspect ratio 0.8+) use landscape layout
      // This allows up to ~100-150px height difference depending on screen size
      isLandscape = true;
    } else {
      isLandscape =
        aspectRatio > PixiSettings.DEVICE_DETECTION.LANDSCAPE_ASPECT_RATIO;
    }

    if (deviceType === PixiSettings.DEVICE.DESKTOP) {
      orientation = PixiSettings.ORIENTATION.LANDSCAPE;
      layout =
        width <= PixiSettings.ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH
          ? PixiSettings.LAYOUT_TYPES.SMALL_DESKTOP
          : PixiSettings.LAYOUT_TYPES.DESKTOP;
    } else {
      orientation = isLandscape
        ? PixiSettings.ORIENTATION.LANDSCAPE
        : PixiSettings.ORIENTATION.PORTRAIT;

      if (deviceType === PixiSettings.DEVICE.MOBILE) {
        layout = isLandscape
          ? PixiSettings.LAYOUT_TYPES.MOBILE_LANDSCAPE
          : PixiSettings.LAYOUT_TYPES.MOBILE_PORTRAIT;
      } else {
        layout = isLandscape
          ? PixiSettings.LAYOUT_TYPES.TABLET_LANDSCAPE
          : PixiSettings.LAYOUT_TYPES.TABLET_PORTRAIT;
      }
    }

    return {
      config: PixiSettings.LAYOUT_CONFIGS[layout],
      device: deviceType,
      orientation,
      layout,
    };
  }, [dimensions]);

  // Calculate symbol dimensions and scaling for the game reel area
  const symbolDimensions = useMemo(() => {
    // Use reelset-specific layout configs with reduced padding for bigger symbols
    let config = PixiSettings.REELSET_LAYOUT_CONFIGS[aspectRatioMode.layout];
    if (!config) {
      console.warn(
        `Reelset layout config not found for: ${aspectRatioMode.layout}`
      );
      // Fallback to minimal padding
      config = { paddingSides: 0, paddingTop: 0, paddingBottom: 0 };
    }
    const numDividers = Settings.REEL_COUNT - 1;
    const totalDividerWidth = PixiSettings.DIVIDER_MAX_WIDTH * numDividers;

    const nativeGameWidth =
      Settings.REEL_COUNT * Settings.SYMBOL_WIDTH + totalDividerWidth;
    const nativeGameHeight = Settings.SYMBOLS_VISIBLE * Settings.SYMBOL_HEIGHT;

    const paddingWidth = config.paddingSides;
    const paddingHeight = config.paddingTop + config.paddingBottom;
    const totalNeededWidth = nativeGameWidth + paddingWidth;
    const totalNeededHeight = nativeGameHeight + paddingHeight;

    let scaleFactor = Math.min(
      dimensions.width / totalNeededWidth,
      dimensions.height / totalNeededHeight
    );

    scaleFactor = Math.min(1, scaleFactor);

    const symbolWidth = Math.round(Settings.SYMBOL_WIDTH * scaleFactor);
    const symbolHeight = Math.round(Settings.SYMBOL_HEIGHT * scaleFactor);

    return {
      symbolWidth,
      symbolHeight,
      scaleFactor,
      totalNeededWidth,
      totalNeededHeight,
      paddingWidth,
      paddingHeight,
    };
  }, [dimensions.width, dimensions.height, aspectRatioMode]);

  // Calculate divider dimensions between reels
  const dividerDimensions = useMemo(() => {
    // DISABLED: Dividers are currently disabled (width set to 0)
    // To re-enable, uncomment the calculation below and remove the return statement
    return { width: 0, height: 0 };

    // const scaledWidth =
    //   (PixiSettings.DIVIDER_MAX_WIDTH * symbolDimensions.symbolWidth) /
    //   Settings.SYMBOL_WIDTH;
    // const width = Math.min(
    //   PixiSettings.DIVIDER_MAX_WIDTH,
    //   Math.round(scaledWidth)
    // );
    // const height = symbolDimensions.symbolHeight * Settings.SYMBOLS_VISIBLE;
    // return { width, height };
  }, []);

  // UI-specific dimensions using GRID (static reference for UI elements)
  const uiDimensions = useMemo(() => {
    const config = aspectRatioMode.config;
    const numDividers = Settings.REEL_COUNT - 1;
    // Hardcode UI dividers to 8px (original game value) for consistent scaling
    const totalDividerWidth = 8 * numDividers;

    const nativeGameWidth =
      Settings.REEL_COUNT * Settings.GRID_SYMBOL_WIDTH + totalDividerWidth;
    // Hardcode UI symbols visible to 3 for consistent toolbar/UI scaling
    const nativeGameHeight = 3 * Settings.GRID_SYMBOL_HEIGHT;

    const paddingWidth = config.paddingSides;
    const paddingHeight = config.paddingTop + config.paddingBottom;
    const totalNeededWidth = nativeGameWidth + paddingWidth;
    const totalNeededHeight = nativeGameHeight + paddingHeight;

    let scaleFactor = Math.min(
      dimensions.width / totalNeededWidth,
      dimensions.height / totalNeededHeight
    );

    scaleFactor = Math.min(1, scaleFactor);

    const symbolWidth = Math.round(Settings.GRID_SYMBOL_WIDTH * scaleFactor);
    const symbolHeight = Math.round(Settings.GRID_SYMBOL_HEIGHT * scaleFactor);

    return {
      symbolWidth,
      symbolHeight,
      scaleFactor,
      totalNeededWidth,
      totalNeededHeight,
      paddingWidth,
      paddingHeight,
    };
  }, [dimensions.width, dimensions.height, aspectRatioMode]);

  // UI-specific divider dimensions
  const uiDividerDimensions = useMemo(() => {
    const scaledWidth =
      (PixiSettings.DIVIDER_MAX_WIDTH * uiDimensions.symbolWidth) /
      Settings.GRID_SYMBOL_WIDTH;
    const width = Math.min(
      PixiSettings.DIVIDER_MAX_WIDTH,
      Math.round(scaledWidth)
    );
    const height = uiDimensions.symbolHeight * Settings.SYMBOLS_VISIBLE;
    return { width, height };
  }, [uiDimensions.symbolWidth, uiDimensions.symbolHeight]);

  // Responsive configuration helper
  const getResponsiveConfig = useMemo(() => {
    return (config, useUIDimensions = false) => {
      // Choose which dimensions to use
      const dims = useUIDimensions ? uiDimensions : symbolDimensions;
      const baseHeight = useUIDimensions
        ? Settings.GRID_SYMBOL_HEIGHT
        : Settings.SYMBOL_HEIGHT;

      let deviceConfig;
      if (aspectRatioMode.device === PixiSettings.DEVICE.DESKTOP) {
        deviceConfig = config.DEVICE_CONFIGS[aspectRatioMode.device];
      } else {
        deviceConfig =
          config.DEVICE_CONFIGS[aspectRatioMode.device]?.[
            aspectRatioMode.orientation
          ]; // Desktop uses landscape orientation by default
      }

      if (!deviceConfig) {
        console.warn(
          `Device config not found for: ${aspectRatioMode.device}/${aspectRatioMode.orientation}`
        );
        return {};
      }

      // Merge with defaults if they exist
      if (config.DEFAULTS) {
        deviceConfig = { ...config.DEFAULTS, ...deviceConfig };
      }

      let scaledWidth, scaledHeight;
      const symbolScale = dims.symbolHeight / baseHeight;

      if (config.BASE_PORTRAIT_WIDTH && config.BASE_LANDSCAPE_WIDTH) {
        const shouldUsePortrait =
          (aspectRatioMode.orientation === PixiSettings.ORIENTATION.PORTRAIT &&
            aspectRatioMode.device === PixiSettings.DEVICE.MOBILE) ||
          aspectRatioMode.device === PixiSettings.DEVICE.TABLET;

        const baseWidth = shouldUsePortrait
          ? config.BASE_PORTRAIT_WIDTH
          : config.BASE_LANDSCAPE_WIDTH;
        const baseHeightValue = shouldUsePortrait
          ? config.BASE_PORTRAIT_HEIGHT
          : config.BASE_LANDSCAPE_HEIGHT;

        scaledWidth = Math.round(baseWidth * deviceConfig.deviceMultiplier * symbolScale);
        scaledHeight = Math.round(
          baseHeightValue * deviceConfig.deviceMultiplier * symbolScale
        );
      } else {
        scaledWidth = Math.round(
          config.BASE_WIDTH * deviceConfig.deviceMultiplier * symbolScale
        );
        scaledHeight = Math.round(
          config.BASE_HEIGHT * deviceConfig.deviceMultiplier * symbolScale
        );
      }

      let fontSize;
      if (config.FONT_SIZE_RATIO) {
        fontSize = Math.floor(scaledHeight * config.FONT_SIZE_RATIO);
      }

      return {
        width: scaledWidth,
        height: scaledHeight,
        fontSize,
        ...deviceConfig,
      };
    };
  }, [
    aspectRatioMode.device,
    aspectRatioMode.orientation,
    symbolDimensions,
    uiDimensions,
  ]);

  // Consolidated context value
  const value = useMemo(() => {
    const gameScale = symbolDimensions.symbolHeight / Settings.SYMBOL_HEIGHT;
    const uiScale = uiDimensions.symbolHeight / Settings.GRID_SYMBOL_HEIGHT;

    return {
      dimensions,

      // Game dimensions (adapts to actual SYMBOL_WIDTH/HEIGHT)
      symbolWidth: symbolDimensions.symbolWidth,
      symbolHeight: symbolDimensions.symbolHeight,
      dividerDimensions,
      reelSpacing: symbolDimensions.symbolWidth + dividerDimensions.width,
      symbolScale: gameScale,

      // UI dimensions (static based on GRID_SYMBOL_WIDTH/HEIGHT)
      uiSymbolWidth: uiDimensions.symbolWidth,
      uiSymbolSize: uiDimensions.symbolWidth,
      uiSymbolHeight: uiDimensions.symbolHeight,
      uiDividerDimensions,
      uiReelSpacing: uiDimensions.symbolWidth + uiDividerDimensions.width,
      uiSymbolScale: uiScale,

      device: aspectRatioMode.device,
      orientation: aspectRatioMode.orientation,
      isMobilePortrait:
        aspectRatioMode.device === PixiSettings.DEVICE.MOBILE &&
        aspectRatioMode.orientation === PixiSettings.ORIENTATION.PORTRAIT,

      getResponsiveConfig,
    };
  }, [
    dimensions,
    symbolDimensions,
    dividerDimensions,
    uiDimensions,
    uiDividerDimensions,
    aspectRatioMode,
    getResponsiveConfig,
  ]);

  return (
    <DimensionsContext.Provider value={value}>
      {children}
    </DimensionsContext.Provider>
  );
};

// Hook for consuming the DimensionsContext
export const useDimensions = () => {
  const context = useContext(DimensionsContext);
  if (!context) {
    throw new Error("useDimensions must be used within a DimensionsProvider");
  }
  return context;
};
